import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  disableVehicleMarketSourceSchedule,
  enableVehicleMarketSourceSchedule,
  formatVehicleMarketActionError,
  getVehicleMarketSourceRuns,
  getVehicleMarketSources,
  runVehicleMarketSource,
  runVehicleMarketSourceDiscovery,
  updateVehicleMarketSource,
} from "../../../../api/vehicleMarketSourceApi";
import { pollAutomotiveIngestUntilComplete } from "../../../../services/pollAutomotiveIngestUntilComplete";
import AdminBtn from "../layout/AdminBtn";
import AdminStatePanel from "../layout/AdminStatePanel";
import DataSourceControlsGrid from "../dataSources/DataSourceControlsGrid";
import { mapVehicleMarketSourceToGridRow } from "../dataSources/mapDataSourceGridRow";
import VehicleMarketScheduleModal from "./VehicleMarketScheduleModal";
import VehicleMarketRunsModal from "./VehicleMarketRunsModal";
import VehicleMarketSourceDetailPanel from "./VehicleMarketSourceDetailPanel";
import {
  VEHICLE_ONLY_DISCOVERY_DISABLED_TOOLTIP,
  filterVehicleMarketSources,
  VEHICLE_MARKET_SCHEDULE_INTERVALS,
  isVehicleOnlyRow,
  toAutomotivePipelineShape,
  toRunsModalSourceShape,
} from "./vehicleMarketSourceFormat";
import { useAuth } from "../../../../auth/useAuth";
import { ADMIN_PERMISSIONS } from "../../../../auth/adminPermissions";

const TYPE_FILTER_OPTIONS = [
  { value: "all", label: "All types" },
  { value: "marketplace", label: "Marketplace" },
  { value: "dealer", label: "Dealer" },
  { value: "reference", label: "Reference" },
  { value: "repair_reference", label: "Repair reference" },
  { value: "manufacturer", label: "Manufacturer" },
  { value: "new_vehicle_price_source", label: "New vehicle price" },
  { value: "unknown", label: "Unknown" },
];

const STATUS_FILTER_OPTIONS = [
  { value: "all", label: "All statuses" },
  { value: "running", label: "Running" },
  { value: "healthy", label: "Healthy" },
  { value: "failed", label: "Failed" },
  { value: "disabled", label: "Disabled" },
  { value: "blocked", label: "Blocked" },
  { value: "retired", label: "Retired" },
  { value: "deferred", label: "Deferred" },
  { value: "partner_feed", label: "Partner feed preferred" },
  { value: "partner_access_required", label: "Partner access required" },
  { value: "reference_only", label: "Reference only" },
  { value: "do_not_use", label: "Do not use" },
  { value: "inventory_only", label: "Inventory only" },
  { value: "discovery_only", label: "Discovery only" },
  { value: "not_wired", label: "Not wired" },
  { value: "unknown", label: "Unknown" },
];

const EXCLUSION_FILTER_OPTIONS = [
  { value: "all", label: "All dispositions" },
  { value: "active", label: "Active" },
  { value: "limited_schedule", label: "Limited schedule" },
  { value: "manual_only", label: "Manual only" },
  { value: "partner_feed", label: "Partner feed" },
  { value: "partner_access_required", label: "Partner access required" },
  { value: "deferred", label: "Deferred" },
  { value: "blocked", label: "Blocked" },
  { value: "retired", label: "Retired" },
  { value: "reference_only", label: "Reference only" },
  { value: "planning_only", label: "Planning only" },
  { value: "inventory_active", label: "Inventory only (OEM)" },
  { value: "do_not_use", label: "Do not use" },
];

const COMPLIANCE_FILTER_OPTIONS = [
  { value: "all", label: "All compliance" },
  { value: "allowed", label: "Allowed" },
  { value: "needs_review", label: "Needs review" },
  { value: "blocked", label: "Blocked" },
  { value: "unknown", label: "Unknown" },
];

const SCHEDULE_FILTER_OPTIONS = [
  { value: "all", label: "All schedule" },
  { value: "enabled", label: "Enabled" },
  { value: "manual", label: "Manual" },
  { value: "disabled", label: "Disabled" },
  { value: "unsupported", label: "Not supported" },
];

const REGISTRY_FILTER_OPTIONS = [
  { value: "all", label: "All registry" },
  { value: "paired", label: "Paired" },
  { value: "ingest_only", label: "Ingest only" },
  { value: "inventory_only", label: "Inventory only" },
];

const VehicleMarketSourceControlsTable = ({
  showToast,
  initialErrorMessage = "",
  onRefresh,
  sources: sourcesProp,
  filters: hubFilters,
  compact = false,
  hideEmpty = false,
}) => {
  const isControlled = Array.isArray(sourcesProp);
  const [sources, setSources] = useState(sourcesProp || []);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(!isControlled);
  const [errorMessage, setErrorMessage] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [scheduleRow, setScheduleRow] = useState(null);
  const [scheduleSaving, setScheduleSaving] = useState(false);
  const [runsRow, setRunsRow] = useState(null);
  const [runs, setRuns] = useState([]);
  const [runsLoading, setRunsLoading] = useState(false);
  const [runsError, setRunsError] = useState("");
  const [runsEmptyMessage, setRunsEmptyMessage] = useState("");
  const [detailRow, setDetailRow] = useState(null);
  const pollAbortRef = useRef(null);

  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [complianceFilter, setComplianceFilter] = useState("all");
  const [scheduleFilter, setScheduleFilter] = useState("all");
  const [registryFilter, setRegistryFilter] = useState("all");
  const [exclusionFilter, setExclusionFilter] = useState("all");

  const { hasPermission, isSuperAdmin } = useAuth();
  const canManageSources =
    isSuperAdmin || hasPermission(ADMIN_PERMISSIONS.REAL_ESTATE_SOURCES_MANAGE);

  const notify = useCallback(
    (message, tone = "success") => {
      if (showToast) {
        showToast(message, tone);
      }
    },
    [showToast]
  );

  const loadCatalog = useCallback(async ({ silent = false } = {}) => {
    if (!silent) {
      setLoading(true);
      setErrorMessage("");
    }
    try {
      const data = await getVehicleMarketSources();
      setSources(data.sources || []);
      setSummary(data.summary || null);
      setErrorMessage("");
    } catch (err) {
      setErrorMessage(err.message || "Unable to load Vehicle Market sources.");
      setSources([]);
      setSummary(null);
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isControlled) {
      setSources(sourcesProp);
      setLoading(false);
    }
  }, [isControlled, sourcesProp]);

  useEffect(() => {
    if (!isControlled) {
      loadCatalog();
    }
    return () => pollAbortRef.current?.abort();
  }, [isControlled, loadCatalog]);

  const filters = useMemo(() => {
    if (compact) {
      return {
        search: hubFilters?.search || "",
        statusFilter: hubFilters?.statusFilter || "all",
        enabledFilter: hubFilters?.enabledFilter || "all",
        typeFilter: "all",
        complianceFilter: "all",
        scheduleFilter: "all",
        registryFilter: "all",
        exclusionFilter: "all",
      };
    }

    return {
      search,
      typeFilter,
      statusFilter,
      complianceFilter,
      scheduleFilter,
      registryFilter,
      exclusionFilter,
    };
  }, [
    compact,
    hubFilters?.search,
    hubFilters?.statusFilter,
    hubFilters?.enabledFilter,
    search,
    typeFilter,
    statusFilter,
    complianceFilter,
    scheduleFilter,
    registryFilter,
    exclusionFilter,
  ]);

  const filteredSources = useMemo(
    () => filterVehicleMarketSources(sources, filters).map((row) => mapVehicleMarketSourceToGridRow(row)),
    [sources, filters]
  );

  const patchRowInList = useCallback((rowId, patch) => {
    setSources((current) =>
      current.map((row) => (row.id === rowId ? { ...row, ...patch } : row))
    );
  }, []);

  const handleRunNow = async (row) => {
    if (!row.id || !row.capabilities?.supportsIngestRun) return;

    setBusyId(row.id);
    pollAbortRef.current?.abort();
    pollAbortRef.current = new AbortController();

    try {
      const result = await runVehicleMarketSource(row.id);
      notify(result.message || `Manual run started for ${row.displayName}.`, "success");
      patchRowInList(row.id, {
        isRunning: true,
        healthStatus: "running",
        lastRunStatus: "running",
      });

      const pollKey = result.pollSourceKey || row.automotiveSourceKey;
      if (pollKey) {
        await pollAutomotiveIngestUntilComplete(pollKey, {
          signal: pollAbortRef.current.signal,
          onUpdate: (automotiveSource) => {
            patchRowInList(row.id, {
              isRunning: automotiveSource.isRunning,
              healthStatus: automotiveSource.health,
              lastRunStatus: automotiveSource.lastStatus,
              lastRunAt: automotiveSource.lastRunAt,
              lastRecordCount: automotiveSource.recordCount,
            });
          },
        });
      }

      notify(`Ingestion completed for ${row.displayName}.`, "success");
      loadCatalog({ silent: true });
      onRefresh?.({ silent: true });
    } catch (error) {
      notify(
        formatVehicleMarketActionError(error) ||
          `Unable to start ingestion for ${row.displayName}.`,
        "error"
      );
      loadCatalog({ silent: true });
    } finally {
      setBusyId(null);
    }
  };

  const handleToggleActive = async (row) => {
    if (!canManageSources || !row.id) {
      return;
    }

    const nextActive = !row.isActive;
    setBusyId(row.id);

    try {
      await updateVehicleMarketSource(row.id, {
        isActive: nextActive,
        enabled: nextActive,
      });
      patchRowInList(row.id, { isActive: nextActive });
      notify(
        nextActive ? `${row.displayName} enabled.` : `${row.displayName} disabled.`,
        "success"
      );
      loadCatalog({ silent: true });
      onRefresh?.({ silent: true });
    } catch (error) {
      notify(formatVehicleMarketActionError(error) || "Unable to update source.", "error");
      loadCatalog({ silent: true });
    } finally {
      setBusyId(null);
    }
  };

  const handleRunDiscovery = async (row) => {
    if (!row.id || !row.capabilities?.supportsDiscovery) return;
    setBusyId(row.id);
    try {
      const result = await runVehicleMarketSourceDiscovery(row.id, { limit: 25 });
      notify(result.message || `Discovery started for ${row.displayName}.`, "success");
      loadCatalog({ silent: true });
    } catch (error) {
      notify(formatVehicleMarketActionError(error) || "Discovery failed.", "error");
    } finally {
      setBusyId(null);
    }
  };

  const handleSaveSchedule = async (row, payload) => {
    if (!row?.id) return;
    setScheduleSaving(true);
    try {
      if (payload.scheduleEnabled === false) {
        await disableVehicleMarketSourceSchedule(row.id);
      } else if (payload.scheduleEnabled === true) {
        await enableVehicleMarketSourceSchedule(row.id, {
          intervalMinutes: payload.scheduleIntervalMinutes,
          scheduleIntervalMinutes: payload.scheduleIntervalMinutes,
        });
      } else {
        await updateVehicleMarketSource(row.id, payload);
      }
      setScheduleRow(null);
      notify(`Schedule updated for ${row.displayName}.`, "success");
      loadCatalog({ silent: true });
    } catch (error) {
      notify(formatVehicleMarketActionError(error) || "Unable to save schedule.", "error");
      throw error;
    } finally {
      setScheduleSaving(false);
    }
  };

  const handleViewRuns = async (row) => {
    setRunsRow(toRunsModalSourceShape(row));
    setRunsLoading(true);
    setRunsError("");
    setRunsEmptyMessage("");
    try {
      const data = await getVehicleMarketSourceRuns(row.id, { limit: 25 });
      setRuns(data.records || []);
      setRunsEmptyMessage(
        data.message ||
          (row.automotiveSourceKey
            ? `No ${row.displayName} manual ingest runs yet.`
            : "No production ingest history for this inventory-only source.")
      );
    } catch (error) {
      setRuns([]);
      setRunsError(formatVehicleMarketActionError(error) || "Unable to load ingestion runs.");
    } finally {
      setRunsLoading(false);
    }
  };

  const displayErrorMessage = errorMessage || initialErrorMessage;

  if (loading && !compact) {
    return <AdminStatePanel message="Loading Vehicle Market sources…" />;
  }

  if (displayErrorMessage && !sources.length) {
    if (hideEmpty) {
      return null;
    }
    return (
      <AdminStatePanel
        variant="error"
        message={displayErrorMessage}
        onRetry={() => (isControlled ? onRefresh?.() : loadCatalog())}
      />
    );
  }

  if (!filteredSources.length) {
    if (hideEmpty) {
      return null;
    }
    if (!sources.length) {
      return <AdminStatePanel message="No Vehicle Market sources are registered yet." />;
    }
  }

  if (!compact && !sources.length) {
    return <AdminStatePanel message="No Vehicle Market sources are registered yet." />;
  }

  return (
    <>
      {displayErrorMessage && !compact ? (
        <p className="admin-data-health-error admin-source-controls-dash__type-note">
          {displayErrorMessage}
        </p>
      ) : null}
      {summary && !compact ? (
        <div className="admin-summary-cards admin-summary-cards--secondary">
          <div className="admin-summary-card">
            <span>Total</span>
            <strong>{summary.total}</strong>
          </div>
          <div className="admin-summary-card">
            <span>Paired</span>
            <strong>{summary.paired}</strong>
          </div>
          <div className="admin-summary-card">
            <span>Ingest only</span>
            <strong>{summary.automotiveOnly}</strong>
          </div>
          <div className="admin-summary-card">
            <span>Inventory only</span>
            <strong>{summary.inventoryOnly ?? summary.vehicleOnly}</strong>
          </div>
          <div className="admin-summary-card">
            <span>Production ingest</span>
            <strong>{summary.productionIngestConfigured ?? summary.supportsIngestRun}</strong>
          </div>
          {summary.exclusions ? (
            <>
              <div className="admin-summary-card">
                <span>Activation ready</span>
                <strong>{summary.activationCandidates ?? summary.exclusions.activationCandidates}</strong>
              </div>
              <div className="admin-summary-card">
                <span>Blocked / retired</span>
                <strong>
                  {(summary.exclusions.blocked ?? 0) + (summary.exclusions.retired ?? 0)}
                </strong>
              </div>
              <div className="admin-summary-card">
                <span>Partner feed</span>
                <strong>{summary.exclusions.partnerFeed ?? 0}</strong>
              </div>
              <div className="admin-summary-card">
                <span>Reference only</span>
                <strong>{summary.exclusions.referenceOnly ?? 0}</strong>
              </div>
            </>
          ) : null}
        </div>
      ) : null}

      {!compact ? (
      <div className="admin-filters-row admin-filters-row--wrap">
        <label>
          Search
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Source name or key"
          />
        </label>
        <label>
          Type
          <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
            {TYPE_FILTER_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </label>
        <label>
          Status
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            {STATUS_FILTER_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </label>
        <label>
          Compliance
          <select value={complianceFilter} onChange={(e) => setComplianceFilter(e.target.value)}>
            {COMPLIANCE_FILTER_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </label>
        <label>
          Schedule
          <select value={scheduleFilter} onChange={(e) => setScheduleFilter(e.target.value)}>
            {SCHEDULE_FILTER_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </label>
        <label>
          Registry
          <select value={registryFilter} onChange={(e) => setRegistryFilter(e.target.value)}>
            {REGISTRY_FILTER_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </label>
        <label>
          Disposition
          <select value={exclusionFilter} onChange={(e) => setExclusionFilter(e.target.value)}>
            {EXCLUSION_FILTER_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </label>
        <button type="button" className="crm-btn crm-btn-secondary" onClick={() => loadCatalog()}>
          Refresh
        </button>
      </div>
      ) : null}

      <DataSourceControlsGrid
        sources={filteredSources}
        emptyMessage="No Vehicle Market sources match these filters."
        busyKey={busyId}
        canManageSources={canManageSources}
        onToggleEnabled={handleToggleActive}
        onRunNow={handleRunNow}
        onSaveSchedule={handleSaveSchedule}
        onViewRuns={handleViewRuns}
        sectionLabel="Vehicle market"
        scheduleIntervals={VEHICLE_MARKET_SCHEDULE_INTERVALS}
        deleteDomain="vehicle_market"
        onDeleted={() => {
          loadCatalog();
          onRefresh?.();
        }}
        extraActions={(row, { isBusy }) => (
          <>
            {row.capabilities?.supportsDiscovery || isVehicleOnlyRow(row) ? (
              <AdminBtn
                variant="secondary"
                className={
                  isBusy || !canManageSources || !row.capabilities?.supportsDiscovery
                    ? " admin-btn--unavailable"
                    : ""
                }
                aria-disabled={
                  isBusy || !canManageSources || !row.capabilities?.supportsDiscovery || undefined
                }
                title={
                  row.capabilities?.supportsDiscovery
                    ? "Run vehicle discovery"
                    : VEHICLE_ONLY_DISCOVERY_DISABLED_TOOLTIP
                }
                onClick={() => {
                  if (isBusy || !canManageSources || !row.capabilities?.supportsDiscovery) {
                    return;
                  }
                  handleRunDiscovery(row);
                }}
              >
                Discovery
              </AdminBtn>
            ) : null}
            <AdminBtn variant="ghost" disabled={isBusy} onClick={() => setDetailRow(row)}>
              Details
            </AdminBtn>
          </>
        )}
      />

      <VehicleMarketScheduleModal
        source={scheduleRow ? toAutomotivePipelineShape(scheduleRow) : null}
        readinessRecord={
          scheduleRow?.scheduleBlocker && scheduleRow.scheduleBlocker !== "none"
            ? {
                compliance: { blocksSchedule: true },
                regressionWarnings: [{ message: scheduleRow.scheduleBlocker }],
              }
            : null
        }
        isOpen={Boolean(scheduleRow)}
        isSaving={scheduleSaving}
        onClose={() => setScheduleRow(null)}
        onSave={(_src, payload) => handleSaveSchedule(scheduleRow, payload)}
      />

      <VehicleMarketRunsModal
        source={runsRow}
        runs={runs}
        isOpen={Boolean(runsRow)}
        isLoading={runsLoading}
        errorMessage={runsError}
        emptyMessage={runsEmptyMessage}
        onClose={() => {
          setRunsRow(null);
          setRuns([]);
          setRunsError("");
          setRunsEmptyMessage("");
        }}
      />

      {detailRow ? (
        <VehicleMarketSourceDetailPanel row={detailRow} onClose={() => setDetailRow(null)} />
      ) : null}
    </>
  );
};

export default VehicleMarketSourceControlsTable;
